/**
 * SoleadLab 2.0 - Map Store
 *
 * Manages map state including view, draw mode, and selected features.
 */

import { create } from 'zustand';
import type { Map as MapboxMap } from 'mapbox-gl';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';

/**
 * Draw modes for indication creation
 */
export type DrawMode = 'simple_select' | 'draw_polygon' | 'draw_point' | 'draw_line_string';

/**
 * Map layer visibility
 */
export interface LayerVisibility {
  indications: boolean;
  overlays: boolean;
  orthophotos: boolean;
  flightPaths: boolean;
  geoPhotos: boolean;
  videoStrips: boolean;
  dataPoints: boolean;
  layoutLabels: boolean;
  /** Derived string lines (sharp-labels WP-C) — independent of the module
   *  grid toggle so the user can choose grid only, lines only, or both. */
  stringLines: boolean;
  layoutImage: boolean;
}

/**
 * Per-level visibility for layout labels.
 * Dynamic: supports both standard levels and custom levels (e.g., "breaker").
 */
export type LayoutLabelLevelVisibility = Record<string, boolean>;

/**
 * Indication type filter
 */
export interface IndicationFilter {
  [key: string]: boolean;
}

/**
 * Historical view mode for indications (Build B):
 *  - 'current'  → apply the resolution overlay (✓ badge on resolved faults keeps
 *                 the type color; legend shows "· N resolved"; Show-resolved can
 *                 declutter). The live "what still needs work" view.
 *  - 'asFound'  → ignore resolution entirely; every fault renders as the
 *                 inspection first found it (no ✓, no grey, resolved always shown).
 *                 The historical "what was found at this inspection" snapshot.
 */
export type IndicationViewMode = 'asFound' | 'current';
/** Who last touched a mark — the FL review lens (see indicationFieldLens). */
export type IndicationFieldLens = 'all' | 'field' | 'operator';

export interface MapState {
  // Map instance
  map: MapboxMap | null;
  draw: MapboxDraw | null;
  isMapReady: boolean;

  // View state
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;

  // Cursor position
  cursorLng: number | null;
  cursorLat: number | null;

  // GPS location tracking
  isLocationTracking: boolean;
  locationStatus: string;
  /** heading = GPS course over ground, degrees clockwise from true north —
   *  null on desktop and while stationary (NaN is normalized to null).
   *  speed in m/s, null when the device doesn't report it. */
  userLocation: { lng: number; lat: number; accuracy: number; heading: number | null; speed: number | null } | null;
  /** Field navigation target: a line is drawn from the user dot to this point.
   *  Session-only — never persisted. */
  guideTarget: { lng: number; lat: number; label?: string } | null;
  /** Guide-to-point mode: the next map click sets guideTarget (mirrors the
   *  add-mode click-interception pattern; mutually exclusive with add modes). */
  guidePointMode: boolean;
  /** Follow mode: camera eases to each GPS fix; broken by a manual drag. */
  isFollowing: boolean;

  // Draw mode
  drawMode: DrawMode;
  isDrawing: boolean;

  // Selection (single and multi-select)
  selectedFeatureId: string | null;
  selectedFeatureIds: string[]; // For multi-select with Ctrl+click
  hoveredFeatureId: string | null;
  /** Area-select mode: the next drawn polygon acts as a selection lasso —
   *  every visible indication whose centroid falls inside it gets selected. */
  areaSelectMode: boolean;

  // Layer visibility
  layerVisibility: LayerVisibility;

  // Layout label level visibility
  layoutLabelLevels: LayoutLabelLevelVisibility;

  /** Show layout grid + string lines at EVERY zoom, ignoring the reveal
   *  ladder — the whole-site assessment mode for first-time positioning
   *  (owner design 2026-08-26). Labels keep their ladder regardless: at
   *  site zoom an every-label carpet is the bug this phase removed. */
  layoutZoomOverride: boolean;

  // Data point alarm state filters (same pattern as layoutLabelLevels)
  dataPointAlarmFilters: Record<string, boolean>;
  // Data point label cards visibility toggle
  dataPointShowLabels: boolean;

  // IV result deviation category filters
  ivResultFilters: Record<string, boolean>;

  // IV per-session visibility (keyed by session ID) and display metric for map labels
  ivSessionVisibility: Record<string, boolean>;
  ivDisplayMetric: string;
  /** IV Results layer master switch (the legend's top checkbox). Independent
   *  of the per-session SELECTION above: switching the layer off/on keeps
   *  which sessions are selected, so a viewer who unticked one session and
   *  then toggles the layer gets the same subset back. Persisted with the
   *  project (owner's saved state = the default a share visitor opens on);
   *  legacy blobs without it derive "on" from any selected session. */
  ivLayerVisible: boolean;

  // Indication filters
  indicationFilters: IndicationFilter;
  /** Phase 1: show resolved indications on the map or hide them (declutter,
   *  Current mode only). */
  indicationShowResolved: boolean;
  /** FL-1 review lens over who last touched a mark:
   *  'all'      — everything (default)
   *  'field'    — ONLY marks a field technician created or edited
   *  'operator' — only office marks, hiding everything the field touched
   *  Session-local by design — a review lens, not a preference, so it is NOT
   *  part of the persisted view prefs. */
  indicationFieldLens: IndicationFieldLens;
  /** Build B: 'current' applies the resolution overlay; 'asFound' shows every
   *  fault as first found (resolution ignored). Global across map + legend. */
  indicationViewMode: IndicationViewMode;

  // Overlay opacity (global fallback)
  overlayOpacity: number;

  // Active overlay for editing (corner markers, etc.)
  activeOverlayId: string | null;

  // Placement-adjust mode for the active overlay — mirrors orthoEditMode.
  // Only while true are the move/scale/rotate handles shown and the baked
  // tiles swapped to the draggable preview image. Entered EXCLUSIVELY from
  // the Plans ticket's Adjust button: a map click must never flip a baked
  // plan to preview (owner: misclicks while editing labels did exactly that).
  overlayAdjustMode: boolean;

  /** Rung-3 macro adjust: the overlay whose DXF vectors are being nudged as
   *  a whole (drag on the map / rotate-scale from the picker). Null = off.
   *  Separate from overlayAdjustMode: that moves the raster sheet, this
   *  moves the CAD layer relative to it. */
  dxfAdjustOverlayId: string | null;

  /** Rung-3 micro adjust: the overlay whose DXF is in local-correction mode
   *  (draw a zone, nudge it), and which zone is selected. */
  dxfZoneOverlayId: string | null;
  dxfSelectedZoneId: string | null;

  /** Plan vectors (rung 3 / R3-D): overlays whose PICKED vector layers are
   *  hidden by the legend's Vectors checkbox. Session-only by design — the
   *  operator's picks (overlay.dxf.picked) stay the durable choice; this is
   *  a viewer's own show/hide on top, identical for a share visitor. */
  hiddenVectorOverlayIds: string[];

  // Overlay edit mode: draw crop rectangle or delete-area polygon directly on the map
  overlayEditMode: 'crop' | 'delete' | null;

  // Active orthophoto for controls (opacity, zoom, remove)
  activeOrthophotoId: string | null;
  // Edit mode: when true, map markers are shown and tiles swap to preview
  orthoEditMode: boolean;

  // Video layer opacity (0..1)
  videoOpacity: number;

  // Batch move geo photos
  batchMoveActive: boolean;
  batchMovePhotoCount: number;
  setBatchMoveState: (active: boolean, count: number) => void;
  // Callbacks registered by useBatchMove hook (not part of reactive state)
  _batchMoveStart?: () => void;
  _batchMoveFinish?: () => void;
  _batchMoveCancel?: () => void;

  // Actions
  setMap: (map: MapboxMap | null) => void;
  setDraw: (draw: MapboxDraw | null) => void;
  setMapReady: (ready: boolean) => void;

  // View actions
  setCenter: (center: [number, number]) => void;
  setZoom: (zoom: number) => void;
  setBearing: (bearing: number) => void;
  setPitch: (pitch: number) => void;
  setView: (view: { center?: [number, number]; zoom?: number; bearing?: number; pitch?: number }) => void;
  flyTo: (center: [number, number], zoom?: number) => void;

  // Cursor actions
  setCursorPosition: (lng: number | null, lat: number | null) => void;

  // Draw actions
  setDrawMode: (mode: DrawMode) => void;
  setDrawing: (drawing: boolean) => void;
  startDrawing: () => void;
  stopDrawing: () => void;
  startPolygonDraw: () => void;

  // Info preview (floating popup near selected indication)
  previewIndicationId: string | null;
  setPreviewIndication: (id: string | null) => void;

  /** Detection ghost popup (fault-detection review). Owned here (not in the
   *  frozen detectionStore contract) so BOTH the map click path (MapCanvas)
   *  and the Detection panel's ranked list can open/close the same popup.
   *  MapCanvas renders the popup and auto-clears the id when the candidate
   *  leaves the visible ghost set. */
  detectionPopupCandId: string | null;
  setDetectionPopupCand: (candId: string | null) => void;

  // Selection actions
  selectFeature: (id: string | null) => void;
  setHoveredFeature: (id: string | null) => void;
  // Multi-select actions (Ctrl+click)
  addToSelection: (id: string) => void;
  removeFromSelection: (id: string) => void;
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  setSelectedFeatureIds: (ids: string[]) => void;
  setAreaSelectMode: (on: boolean) => void;

  // Layer visibility actions
  setLayerVisibility: (layer: keyof LayerVisibility, visible: boolean) => void;
  toggleLayerVisibility: (layer: keyof LayerVisibility) => void;

  // Layout label level visibility actions
  toggleLayoutLabelLevel: (level: string) => void;
  toggleLayoutZoomOverride: () => void;

  // Data point alarm filter actions
  toggleDataPointAlarmFilter: (state: string) => void;
  toggleDataPointShowLabels: () => void;

  // IV result filter actions
  toggleIVResultFilter: (key: string) => void;
  /** Flip one session's selection. Selecting a session also switches the
   *  layer on (a freshly ticked session must be visible, not silently
   *  parked behind an off layer). Deselecting never touches the layer. */
  toggleIVSessionVisibility: (sessionId: string) => void;
  /** Explicit form of the above (same layer auto-on rule). */
  setIVSessionVisibility: (sessionId: string, visible: boolean) => void;
  setAllIVSessionsVisibility: (sessionIds: string[], visible: boolean) => void;
  /** Layer master switch — never modifies the per-session selection. */
  setIVLayerVisible: (visible: boolean) => void;
  setIVDisplayMetric: (metric: string) => void;

  // Indication filter actions
  setIndicationFilter: (type: string, visible: boolean) => void;
  toggleIndicationFilter: (type: string) => void;
  showAllIndications: () => void;
  hideAllIndications: () => void;
  toggleIndicationShowResolved: () => void;
  setIndicationFieldLens: (lens: IndicationFieldLens) => void;
  setIndicationViewMode: (mode: IndicationViewMode) => void;

  // Overlay opacity
  setOverlayOpacity: (opacity: number) => void;

  // Active overlay
  setActiveOverlay: (id: string | null) => void;
  /** Legend "Vectors" checkbox: show/hide one plan's picked vector layers. */
  toggleVectorOverlayVisibility: (overlayId: string) => void;
  enterOverlayAdjustMode: (id: string) => void;
  exitOverlayAdjustMode: () => void;
  setDxfAdjustOverlay: (id: string | null) => void;
  setDxfZoneOverlay: (id: string | null) => void;
  setDxfSelectedZone: (id: string | null) => void;
  setOverlayEditMode: (mode: 'crop' | 'delete' | null) => void;

  // Active orthophoto
  setActiveOrthophoto: (id: string | null) => void;
  enterOrthoEditMode: (id: string) => void;

  // Video opacity
  setVideoOpacity: (opacity: number) => void;

  // GPS location tracking
  startLocationTracking: () => void;
  stopLocationTracking: () => void;
  updateUserLocation: (lng: number, lat: number, accuracy: number, heading: number | null, speed: number | null) => void;
  setLocationStatus: (status: string) => void;

  // Field navigation (guide line + follow mode)
  /** Set a guide target; auto-starts location tracking when it's off. */
  setGuideTarget: (target: { lng: number; lat: number; label?: string } | null) => void;
  clearGuideTarget: () => void;
  setGuidePointMode: (on: boolean) => void;
  setFollowing: (on: boolean) => void;
}

const defaultLayerVisibility: LayerVisibility = {
  indications: true,
  overlays: true,
  orthophotos: true,
  flightPaths: true,
  geoPhotos: true,
  videoStrips: true,
  dataPoints: true,
  layoutLabels: true,
  stringLines: true,
  // Off, with no legend control any more (owner, 2026-09-27: the layout's
  // own raster is not in use — plan overlays are the raster). The render
  // path stays for the flag's sake but never lights up.
  layoutImage: false,
};

const defaultLayoutLabelLevels: LayoutLabelLevelVisibility = {
  station: true,
  inverter: true,
  mppt: true,
  dccb: true,
  string: true,
  module: true,
  row: true,
  zone: true,
  infrastructure: true,
  unclassified: false,
  // Custom levels (e.g., "breaker") default to true when first encountered
};

// Default filters - all types visible (matching INDICATION_TYPES from projectStore)
const defaultIndicationFilters: IndicationFilter = {
  'Diode': true,
  'Hot spots': true,
  'Crack': true,
  'String OC': true,
  'Glass': true,
  'PID': true,
  'O.circuit': true,
  'Opt.OC': true,
  'Opt.SC': true,
  'Riso/Pol': true,
  'Soiling': true,
  'EL': true,
  'Tracker': true,
  'Vegetation': true,
  'Custom': true,
  'IV Curve': true,
  'IR Comp.': true,
  'Video': true,
  'Photo': true,
  'DataPoint': true,
};

// Store geolocation watch ID outside of state (not reactive)
let locationWatchId: number | null = null;

// Screen wake lock while tracking (enhancement only — failures are ignored).
// Sentinel is auto-released by the browser on tab hide; we re-acquire on
// visibility regain while tracking is still on.
let wakeLockSentinel: { release: () => Promise<void> } | null = null;

async function acquireWakeLock(): Promise<void> {
  try {
    const wl = (navigator as unknown as { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock;
    if (!wl?.request) return;
    wakeLockSentinel = await wl.request('screen');
  } catch { /* unsupported / denied (e.g. low battery) — tracking still works */ }
}

function releaseWakeLock(): void {
  try { void wakeLockSentinel?.release(); } catch { /* ignore */ }
  wakeLockSentinel = null;
}

function reacquireWakeLockOnVisible(): void {
  if (document.visibilityState === 'visible' && useMapStore.getState().isLocationTracking) {
    void acquireWakeLock();
  }
}

export const useMapStore = create<MapState>((set, get) => ({
  map: null,
  draw: null,
  isMapReady: false,

  center: [34.8, 31.5],
  zoom: 7,
  bearing: 0,
  pitch: 0,

  cursorLng: null,
  cursorLat: null,

  // GPS location
  isLocationTracking: false,
  locationStatus: 'Location: off',
  userLocation: null,
  guideTarget: null,
  guidePointMode: false,
  isFollowing: false,

  drawMode: 'simple_select',
  isDrawing: false,

  selectedFeatureId: null,
  selectedFeatureIds: [],
  hoveredFeatureId: null,
  areaSelectMode: false,
  previewIndicationId: null,
  detectionPopupCandId: null,

  layerVisibility: defaultLayerVisibility,
  layoutLabelLevels: defaultLayoutLabelLevels,
  layoutZoomOverride: false,
  dataPointAlarmFilters: {},
  dataPointShowLabels: true,
  ivResultFilters: { pass: true, warn: true, fail: true, unknown: true },
  ivSessionVisibility: {},
  ivDisplayMetric: 'result',
  ivLayerVisible: false,
  indicationFilters: defaultIndicationFilters,
  indicationShowResolved: true,
  indicationFieldLens: 'all',
  indicationViewMode: 'current',
  overlayOpacity: 0.8,
  activeOverlayId: null,
  overlayAdjustMode: false,
  dxfAdjustOverlayId: null,
  dxfZoneOverlayId: null,
  dxfSelectedZoneId: null,
  hiddenVectorOverlayIds: [],
  overlayEditMode: null,
  activeOrthophotoId: null,
  orthoEditMode: false,
  videoOpacity: 0.85,

  // Batch move
  batchMoveActive: false,
  batchMovePhotoCount: 0,
  setBatchMoveState: (active, count) => set({ batchMoveActive: active, batchMovePhotoCount: count }),

  setMap: (map) => set({ map }),
  setDraw: (draw) => set({ draw }),
  setMapReady: (ready) => set({ isMapReady: ready }),

  // View actions
  setCenter: (center) => set({ center }),
  setZoom: (zoom) => set({ zoom }),
  setBearing: (bearing) => set({ bearing }),
  setPitch: (pitch) => set({ pitch }),

  setView: (view) =>
    set((state) => ({
      center: view.center ?? state.center,
      zoom: view.zoom ?? state.zoom,
      bearing: view.bearing ?? state.bearing,
      pitch: view.pitch ?? state.pitch,
    })),

  flyTo: (center, zoom) => {
    const { map } = get();
    if (map) {
      map.flyTo({
        center,
        zoom: zoom ?? get().zoom,
        duration: 1500,
      });
    }
    set({ center, zoom: zoom ?? get().zoom });
  },

  // Cursor actions
  setCursorPosition: (lng, lat) => set({ cursorLng: lng, cursorLat: lat }),

  // Draw actions
  setDrawMode: (mode) => set({ drawMode: mode }),
  setDrawing: (drawing) => set({ isDrawing: drawing }),
  startDrawing: () => set({ isDrawing: true }),
  stopDrawing: () => set({ isDrawing: false, drawMode: 'simple_select' }),
  startPolygonDraw: () => set({ isDrawing: true, drawMode: 'draw_polygon' }),

  // Info preview
  setPreviewIndication: (id) => set({ previewIndicationId: id }),
  setDetectionPopupCand: (candId) => set({ detectionPopupCandId: candId }),

  // Selection actions
  selectFeature: (id) => set({ selectedFeatureId: id, selectedFeatureIds: id ? [id] : [] }),
  setHoveredFeature: (id) => set({ hoveredFeatureId: id }),

  // Multi-select actions (Ctrl+click)
  addToSelection: (id) =>
    set((state) => ({
      selectedFeatureIds: state.selectedFeatureIds.includes(id)
        ? state.selectedFeatureIds
        : [...state.selectedFeatureIds, id],
      selectedFeatureId: id, // Keep last selected as primary
    })),

  removeFromSelection: (id) =>
    set((state) => {
      const newIds = state.selectedFeatureIds.filter((fid) => fid !== id);
      return {
        selectedFeatureIds: newIds,
        selectedFeatureId: newIds.length > 0 ? newIds[newIds.length - 1] : null,
      };
    }),

  toggleSelection: (id) =>
    set((state) => {
      const isSelected = state.selectedFeatureIds.includes(id);
      if (isSelected) {
        const newIds = state.selectedFeatureIds.filter((fid) => fid !== id);
        return {
          selectedFeatureIds: newIds,
          selectedFeatureId: newIds.length > 0 ? newIds[newIds.length - 1] : null,
        };
      } else {
        return {
          selectedFeatureIds: [...state.selectedFeatureIds, id],
          selectedFeatureId: id,
        };
      }
    }),

  clearSelection: () => set({ selectedFeatureId: null, selectedFeatureIds: [] }),

  setSelectedFeatureIds: (ids) =>
    set({
      selectedFeatureIds: ids,
      selectedFeatureId: ids.length > 0 ? ids[ids.length - 1] : null,
    }),

  setAreaSelectMode: (on) => set({ areaSelectMode: on }),

  // Layer visibility actions
  setLayerVisibility: (layer, visible) =>
    set((state) => ({
      layerVisibility: { ...state.layerVisibility, [layer]: visible },
    })),

  toggleLayerVisibility: (layer) =>
    set((state) => ({
      layerVisibility: {
        ...state.layerVisibility,
        [layer]: !state.layerVisibility[layer],
      },
    })),

  toggleLayoutZoomOverride: () =>
    set((state) => ({ layoutZoomOverride: !state.layoutZoomOverride })),

  toggleLayoutLabelLevel: (level) =>
    set((state) => ({
      layoutLabelLevels: {
        ...state.layoutLabelLevels,
        [level]: !(state.layoutLabelLevels[level] ?? true),
      },
    })),

  // Data point alarm filter actions
  toggleDataPointAlarmFilter: (alarmState) =>
    set((state) => ({
      dataPointAlarmFilters: {
        ...state.dataPointAlarmFilters,
        [alarmState]: !(state.dataPointAlarmFilters[alarmState] ?? true),
      },
    })),
  toggleDataPointShowLabels: () =>
    set((state) => ({ dataPointShowLabels: !state.dataPointShowLabels })),

  toggleIVResultFilter: (key) =>
    set((state) => ({
      ivResultFilters: {
        ...state.ivResultFilters,
        [key]: !(state.ivResultFilters[key] ?? true),
      },
    })),

  toggleIVSessionVisibility: (sessionId) =>
    set((state) => {
      const next = !(state.ivSessionVisibility[sessionId] ?? false);
      return {
        ivSessionVisibility: { ...state.ivSessionVisibility, [sessionId]: next },
        // Selecting a session switches the layer on; deselecting leaves it.
        ...(next ? { ivLayerVisible: true } : {}),
      };
    }),

  setIVSessionVisibility: (sessionId, visible) =>
    set((state) => ({
      ivSessionVisibility: { ...state.ivSessionVisibility, [sessionId]: visible },
      ...(visible ? { ivLayerVisible: true } : {}),
    })),

  setAllIVSessionsVisibility: (sessionIds, visible) =>
    set((state) => ({
      ivSessionVisibility: {
        ...state.ivSessionVisibility,
        ...Object.fromEntries(sessionIds.map((id) => [id, visible])),
      },
      ...(visible ? { ivLayerVisible: true } : {}),
    })),

  setIVLayerVisible: (visible) => set({ ivLayerVisible: visible }),

  setIVDisplayMetric: (metric) => set({ ivDisplayMetric: metric }),

  // Indication filter actions
  setIndicationFilter: (type, visible) =>
    set((state) => ({
      indicationFilters: { ...state.indicationFilters, [type]: visible },
    })),

  toggleIndicationFilter: (type) =>
    set((state) => ({
      indicationFilters: {
        ...state.indicationFilters,
        [type]: !state.indicationFilters[type],
      },
    })),

  showAllIndications: () => set({ indicationFilters: defaultIndicationFilters }),

  hideAllIndications: () =>
    set({
      indicationFilters: Object.fromEntries(
        Object.keys(defaultIndicationFilters).map((k) => [k, false])
      ),
    }),

  toggleIndicationShowResolved: () =>
    set((state) => ({ indicationShowResolved: !state.indicationShowResolved })),

  setIndicationFieldLens: (lens) => set({ indicationFieldLens: lens }),

  setIndicationViewMode: (mode) => set({ indicationViewMode: mode }),

  // Overlay opacity
  setOverlayOpacity: (opacity) => set({ overlayOpacity: opacity }),

  // Active overlay
  // Selection alone never carries adjust mode along — mirrors
  // setActiveOrthophoto clearing orthoEditMode.
  setActiveOverlay: (id) => set({ activeOverlayId: id, overlayAdjustMode: false }),
  toggleVectorOverlayVisibility: (overlayId) => set((state) => ({
    hiddenVectorOverlayIds: state.hiddenVectorOverlayIds.includes(overlayId)
      ? state.hiddenVectorOverlayIds.filter((id) => id !== overlayId)
      : [...state.hiddenVectorOverlayIds, overlayId],
  })),
  enterOverlayAdjustMode: (id) => set({ activeOverlayId: id, overlayAdjustMode: true }),
  exitOverlayAdjustMode: () => set({ overlayAdjustMode: false }),
  // Mutually exclusive with the raster adjust — two drag handlers competing
  // for the same mousedown is how misclicks become mystery edits.
  setDxfAdjustOverlay: (id) => set({ dxfAdjustOverlayId: id, overlayAdjustMode: false, dxfZoneOverlayId: null }),
  // Macro and micro are mutually exclusive: both bind mousedown, and a drag
  // that means "move everything" in one mode means "draw a zone" in the other.
  setDxfZoneOverlay: (id) => set({
    dxfZoneOverlayId: id, dxfAdjustOverlayId: null, overlayAdjustMode: false,
    dxfSelectedZoneId: null,
  }),
  setDxfSelectedZone: (id) => set({ dxfSelectedZoneId: id }),
  setOverlayEditMode: (mode) => set({ overlayEditMode: mode }),

  // Active orthophoto — selecting shows controls, edit mode shows markers
  setActiveOrthophoto: (id) => set({ activeOrthophotoId: id, orthoEditMode: false }),
  enterOrthoEditMode: (id) => set({ activeOrthophotoId: id, orthoEditMode: true }),

  // Video opacity
  setVideoOpacity: (opacity) => set({ videoOpacity: Math.min(1, Math.max(0, opacity)) }),

  // GPS location tracking
  startLocationTracking: () => {
    if (!navigator.geolocation) {
      set({ locationStatus: 'Location: not supported' });
      return;
    }

    if (locationWatchId !== null) {
      // Already tracking
      return;
    }

    set({ isLocationTracking: true, isFollowing: true, locationStatus: 'Location: starting...' });
    void acquireWakeLock();
    document.addEventListener('visibilitychange', reacquireWakeLockOnVisible);

    locationWatchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy, heading, speed } = position.coords;

        // First fix: fly in close. Later fixes are handled by the follow-mode
        // effect in MapCanvas (ease without touching the user's zoom).
        const isFirstFix = !get().userLocation;

        get().updateUserLocation(
          longitude,
          latitude,
          accuracy,
          typeof heading === 'number' && Number.isFinite(heading) ? heading : null,
          typeof speed === 'number' && Number.isFinite(speed) ? speed : null,
        );

        const { map } = get();
        if (map && isFirstFix) {
          map.flyTo({ center: [longitude, latitude], zoom: 17 });
        }
      },
      (error) => {
        let errorMsg = 'Location: error';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location: permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location: unavailable';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location: timeout';
            break;
        }
        set({ locationStatus: errorMsg, isLocationTracking: false, isFollowing: false });
        document.removeEventListener('visibilitychange', reacquireWakeLockOnVisible);
        releaseWakeLock();
        if (locationWatchId !== null) {
          navigator.geolocation.clearWatch(locationWatchId);
          locationWatchId = null;
        }
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 10000,
      }
    );
  },

  stopLocationTracking: () => {
    if (locationWatchId !== null) {
      navigator.geolocation.clearWatch(locationWatchId);
      locationWatchId = null;
    }
    document.removeEventListener('visibilitychange', reacquireWakeLockOnVisible);
    releaseWakeLock();
    // A guide line with no user dot is meaningless — clear it on stop.
    set({
      isLocationTracking: false,
      locationStatus: 'Location: off',
      userLocation: null,
      guideTarget: null,
      guidePointMode: false,
      isFollowing: false,
    });
  },

  updateUserLocation: (lng, lat, accuracy, heading, speed) =>
    set({
      userLocation: { lng, lat, accuracy, heading, speed },
      locationStatus: `Location: ${lat.toFixed(6)}, ${lng.toFixed(6)} (±${Math.round(accuracy)}m)`,
    }),

  setLocationStatus: (status) => set({ locationStatus: status }),

  // Field navigation
  setGuideTarget: (target) => {
    set({ guideTarget: target, guidePointMode: false });
    if (target && !get().isLocationTracking) {
      get().startLocationTracking();
    }
  },

  clearGuideTarget: () => set({ guideTarget: null }),

  setGuidePointMode: (on) => set({ guidePointMode: on }),

  setFollowing: (on) => {
    set({ isFollowing: on });
    // Re-centering on demand: snap to the last fix immediately, don't wait
    // for the next GPS update.
    if (on) {
      const { map, userLocation } = get();
      if (map && userLocation) {
        map.easeTo({ center: [userLocation.lng, userLocation.lat], duration: 600 });
      }
    }
  },
}));
